<template>
    <v-dialog persistent v-model="dialog" max-width="500">
        <template v-slot:activator="{ on: dialog }">
            <v-btn :disabled="disabled" v-if="activatorType !== 'listItem' && !isIconDialog" v-on="{ ...dialog }" color="primary">
                <v-icon left>mdi-plus</v-icon>
                Token
            </v-btn>
            <v-list-item v-else-if="activatorType === 'listItem'" v-on="{ ...dialog }" @click.stop.prevent>
                <v-list-item-icon>
                    <v-icon color="secondary">{{ activatorIcon || 'mdi-pencil-outline' }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title class="secondary--text mr-5">
                    {{ listItemTitle || (dialogAction === dialogActions.CREATE_NEW_TOKEN ? 'Create token' : 'Edit token') }}
                </v-list-item-title>
            </v-list-item>
            <v-tooltip v-else-if="showTooltip" bottom>
                <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                        :disabled="disabled"
                        :icon="iconActivatorStyle === 'icon'"
                        :text="iconActivatorStyle === 'text'"
                        small
                        :class="iconActivatorStyle === 'text' ? 'px-2' : ''"
                        :min-width="iconActivatorStyle === 'text' ? 0 : undefined"
                        @click.stop.prevent
                        v-on="{ ...tooltip, ...dialog }">
                        <v-icon small>{{ activatorIcon }}</v-icon>
                    </v-btn>
                </template>
                <span>{{ tooltipText || (dialogAction === dialogActions.CREATE_NEW_TOKEN ? 'Create' : 'Edit') }}</span>
            </v-tooltip>
            <v-btn
                v-else
                :disabled="disabled"
                :icon="iconActivatorStyle === 'icon'"
                :text="iconActivatorStyle === 'text'"
                small
                :class="iconActivatorStyle === 'text' ? 'px-2' : ''"
                :min-width="iconActivatorStyle === 'text' ? 0 : undefined"
                @click.stop.prevent
                v-on="{ ...dialog }">
                <v-icon small>{{ activatorIcon }}</v-icon>
            </v-btn>
        </template>
        <v-card>
            <v-card-title class="flex-nowrap align-start">
                <div v-if="dialogAction === dialogActions.CREATE_NEW_TOKEN" class="d-flex align-center secondary--text">Create Token</div>
                <div v-else-if="dialogAction === dialogActions.EDIT_TOKEN" class="d-flex align-center secondary--text">Edit Token</div>

                <v-spacer></v-spacer>
                <v-btn @click="dialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-spacer></v-spacer>

            <v-card-text>
                <v-form v-model="valid" class="mt-6">
                    <div class="d-flex flex-column">
                        <v-select
                            v-model="newTokenScope"
                            :rules="newTokenFormRules"
                            :items="Object.keys(orgTokenScopes)"
                            label="Select token scope*"
                            required
                            outlined></v-select>
                    </div>
                    <div class="d-flex flex-column">
                        <v-select
                            v-model="newTokenType"
                            :rules="newTokenFormRules"
                            :items="Object.keys(orgTokenTypes)"
                            label="Select token type*"
                            required
                            outlined></v-select>
                    </div>
                    <div v-if="newTokenScope && newTokenType && orgTokenTypes[newTokenType] === orgTokenTypes.XPRESSFEED" class="d-flex flex-column" outlined>
                        <v-text-field
                            autocomplete="new-username"
                            v-model="newTokenUsername"
                            :rules="newTokenFormRules"
                            label="Username*"
                            placeholder="Provide token details"
                            required
                            outlined></v-text-field>
                        <v-text-field
                            v-model="newTokenPassword"
                            autocomplete="new-password"
                            :rules="newTokenFormRules"
                            :type="showPassword ? 'text' : 'password'"
                            :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                            label="Password*"
                            required
                            @click:append="showPassword = !showPassword"
                            outlined></v-text-field>
                    </div>
                    <div v-else-if="newTokenScope && newTokenType">
                        <v-text-field
                            autocomplete="new-token"
                            v-model="newTokenValue"
                            :rules="newTokenFormRules"
                            :type="showPassword ? 'text' : 'password'"
                            :append-icon="showPassword ? 'visibility' : 'visibility_off'"
                            label="Token*"
                            required
                            @click:append="showPassword = !showPassword"
                            outlined></v-text-field>
                    </div>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn
                    v-if="dialogAction === dialogActions.CREATE_NEW_TOKEN"
                    :loading="loading"
                    :disabled="!valid"
                    color="primary"
                    text
                    @click="createNewToken">
                    create
                </v-btn>
                <v-btn v-else-if="dialogAction === dialogActions.EDIT_TOKEN" :loading="loading" :disabled="!valid" color="primary" text @click="editToken">
                    confirm
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { enumsData } from '@/mixins/enums'
import { isJson } from '@/utils'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as apiKeysErrors from '@/modules/user/errors/apiKeys'
import * as organizationErrors from '@/modules/home/errors/organizations'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as userErrors from '@/modules/user/errors/settings'

const { getBackendErrorMessage } = createBackendErrorHandler(apiKeysErrors, organizationErrors, permissionsErrors, userErrors)

function defaultData() {
    return {
        dialog: false,
        newTokenFormRules: [v => !!v || 'Required field'],
        valid: false,
        newTokenUsername: '',
        newTokenPassword: '',
        newTokenValue: '',
        newTokenScope: null,
        newTokenType: null,
        loading: false,
        dialogActions: {
            CREATE_NEW_TOKEN: 'createToken',
            EDIT_TOKEN: 'editToken'
        },
        showPassword: false
    }
}
export default {
    name: 'CreateAndEditTokenDialog',
    mixins: [enumsData],
    props: {
        dialogAction: { type: String, default: 'createToken' },
        activatorType: {
            type: String,
            default: ''
        },
        listItemTitle: {
            type: String,
            default: ''
        },
        isIconDialog: {
            type: Boolean,
            default: false
        },
        activatorIcon: { type: String, default: null },
        iconActivatorStyle: {
            type: String,
            default: 'icon'
        },
        showTooltip: { type: Boolean, default: true },
        tooltipText: { type: String, default: '' },
        tokenData: {
            type: Object,
            default: null
        },
        disabled: Boolean
    },
    data() {
        return defaultData()
    },
    mounted() {
        if (this.tokenData) {
            if (this.dialogAction === this.dialogActions.EDIT_TOKEN) {
                this.newTokenScope = Object.keys(this.orgTokenScopes).find(key => this.orgTokenScopes[key] === this.tokenData.scope)
                this.newTokenType = Object.keys(this.orgTokenTypes).find(key => this.orgTokenTypes[key] === this.tokenData.token_type)
            }
            if (isJson(this.tokenData.token)) {
                this.newTokenUsername = JSON.parse(this.tokenData.token).username
                this.newTokenPassword = JSON.parse(this.tokenData.token).password
            } else {
                this.newTokenValue = this.tokenData.token
            }
        }
    },
    methods: {
        createNewToken: function () {
            this.loading = true
            const postBody = {
                token_type: this.orgTokenTypes[this.newTokenType],
                scope: this.orgTokenScopes[this.newTokenScope]
            }
            if (this.orgTokenTypes[this.newTokenType] === this.orgTokenTypes.XPRESSFEED) {
                const tokenString = { username: this.newTokenUsername, password: this.newTokenPassword }
                postBody.token = JSON.stringify(tokenString)
            } else {
                postBody.token = this.newTokenValue
            }
            this.$axios
                .post(`/orgs/${this.$route.params.oid}/org_tokens`, postBody)
                .then(response => {
                    this.$store.dispatch('orgStore/fetchOrgTokens', this.$route.params.oid)
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'New token has been created!',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
                })
                .catch(error => {
                    const snackBarText = getBackendErrorMessage(error, 'Failed to create new token.')
                    if (snackBarText) {
                        this.$store.dispatch('showSnackBar', {
                            snackBarText,
                            snackBarTimeout: 10000,
                            snackBarIcon: 'error'
                        })
                    }
                })
                .finally(() => {
                    this.loading = false
                    this.dialog = false
                })
        },
        editToken: function () {
            this.loading = true
            const patchBody = {
                token_type: this.orgTokenTypes[this.newTokenType],
                scope: this.orgTokenScopes[this.newTokenScope]
            }
            if (this.orgTokenTypes[this.newTokenType] === this.orgTokenTypes.XPRESSFEED) {
                const tokenString = { username: this.newTokenUsername, password: this.newTokenPassword }
                patchBody.token = JSON.stringify(tokenString)
            } else {
                patchBody.token = this.newTokenValue
            }
            this.$axios
                .patch(`/org_tokens/${this.tokenData.otknid}`, patchBody)
                .then(response => {
                    this.$store.dispatch('orgStore/fetchOrgTokens', this.$route.params.oid)
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'Token info has been updated!',
                        snackBarTimeout: 5000,
                        snackBarIcon: 'check_circle'
                    })
                })
                .catch(error => {
                    const snackBarText = getBackendErrorMessage(error, 'Failed to update token info.')
                    if (snackBarText) {
                        this.$store.dispatch('showSnackBar', {
                            snackBarText,
                            snackBarTimeout: 10000,
                            snackBarIcon: 'error'
                        })
                    }
                })
                .finally(() => {
                    this.loading = false
                    this.dialog = false
                })
        }
    },
    watch: {
        dialog: function (nextVal, preVal) {
            if (!nextVal && preVal && this.dialogAction === this.dialogActions.CREATE_NEW_TOKEN) {
                Object.assign(this.$data, defaultData())
            }
        }
    }
}
</script>
